import { useEffect, useState } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import {
  DownloadsStatus,
  DownloadsTableReportsType,
} from '@views/pages/Downloads/hooks';
import { DownloadsReportEventsType } from '@common/types';
import { safeRefreshTokens } from '@config/axios/axios.config.ts';
import { useAuthTokens } from '@store';

export interface DownloadNotificationEventType {
  type: DownloadsReportEventsType;
  payload: {
    report_download_id: string;
    status?: DownloadsStatus;
    percentage?: number;
    generated_at?: string;
    report_name?: string;
    report_type?: DownloadsTableReportsType;
    file_size?: number;
    download_name?: string;
    care_plan_download_id?: string;
    failure_reason?: string;
  };
}

export const useDownloadsNotification = () => {
  const { token } = useAuthTokens();
  const [event, setEvent] = useState<DownloadNotificationEventType | null>(
    null,
  );

  useEffect(() => {
    if (!token) {
      return;
    }

    const controller = new AbortController();
    let isCancelled = false;
    let retryTimeout: number | undefined;
    let retryDelay = 1000;

    const subscribe = async () => {
      try {
        await fetchEventSource(
          `${import.meta.env.VITE_BASE_URL}/reporting/downloads/sse`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
            async onopen(response) {
              if (response.ok) {
                return;
              }

              if (response.status === 401) {
                controller.abort();
                await safeRefreshTokens();
                throw new Error('unauthorized');
              }

              throw new Error(`Unexpected status ${response.status}`);
            },
            onmessage(ev) {
              if (!ev.data) {
                return;
              }

              try {
                const parsed = JSON.parse(ev.data);
                setEvent(parsed);
              } catch (err) {
                // eslint-disable-next-line no-console
                console.log('Downloads SSE error', err);
              }
            },
            onerror(err) {
              // eslint-disable-next-line no-console
              console.log('Downloads SSE error', err);
              throw err;
            },
            openWhenHidden: true,
          },
        );
        retryDelay = 1000;
      } catch (err) {
        if (isCancelled) {
          return;
        }

        const message = (err as Error).message;

        if (message === 'unauthorized') {
          return;
        }

        retryTimeout = window.setTimeout(() => {
          if (!isCancelled) {
            subscribe();
          }
        }, retryDelay);

        retryDelay = Math.min(retryDelay * 2, 30000);
      }
    };

    void subscribe();

    return () => {
      isCancelled = true;
      controller.abort();
      if (retryTimeout) {
        window.clearTimeout(retryTimeout);
      }
    };
  }, [token]);

  return {
    event,
  };
};
